import { useCallback, useRef, useState } from 'react';
import { haptic } from '../lib/haptic';
import { playSfx } from '../lib/sfx';
import './ImageStack.css';

type ImageStackProps = {
  images: string[];
  alt: string;
  onPlay?: () => void;
  className?: string;
};

const TAB_COLORS = ['yellow', 'pink', 'cyan', 'lime'] as const;

/**
 * ImageStack: A true physical-feeling Neobrutalism stacked card deck designed
 * for Clarisa (2 years old). Cards visibly peek out from behind each other with
 * tilted angles, bold black borders, and colorful tabs.
 *
 * Tapping or swiping the cards plays the word audio and shuffles the top card
 * to reveal the next picture in the stack!
 */
export function ImageStack({
  images,
  alt,
  onPlay,
  className = '',
}: ImageStackProps) {
  const [activeIdx, setActiveIdx] = useState(0);
  const [isShuffling, setIsShuffling] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const count = images.length;

  const nextCard = useCallback(() => {
    if (count <= 1 || isShuffling) return;
    haptic('light');
    playSfx('page');
    setIsShuffling(true);

    window.setTimeout(() => {
      setActiveIdx((prev) => (prev + 1) % count);
      setIsShuffling(false);
      setDragOffset(0);
    }, 180);
  }, [count, isShuffling]);

  const selectCard = useCallback(
    (idx: number) => {
      if (idx === activeIdx || isShuffling) return;
      haptic('light');
      playSfx('page');
      setIsShuffling(true);
      window.setTimeout(() => {
        setActiveIdx(idx);
        setIsShuffling(false);
        setDragOffset(0);
      }, 160);
      onPlay?.();
    },
    [activeIdx, isShuffling, onPlay]
  );

  const handleCardTap = useCallback(
    (idx: number) => {
      onPlay?.();
      if (idx === activeIdx) {
        nextCard();
      } else {
        selectCard(idx);
      }
    },
    [onPlay, activeIdx, nextCard, selectCard]
  );

  // Touch swipe support for 2-year-olds who love dragging cards
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const currentX = e.touches[0].clientX;
    setDragOffset(currentX - touchStartX.current);
  };

  const onTouchEnd = () => {
    if (touchStartX.current !== null) {
      if (Math.abs(dragOffset) > 40) {
        onPlay?.();
        nextCard();
      }
    }
    touchStartX.current = null;
    setDragOffset(0);
  };

  if (!images || images.length === 0) return null;

  return (
    <div className={`card-deck-container ${className}`.trim()}>
      <div
        className="card-deck-stack"
        role="region"
        aria-label={`Mazo de fotos de ${alt} (${count} fotos)`}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {images.map((src, i) => {
          // Relative position in the stack (0: front, 1: directly behind, 2: 3rd, etc.)
          const relPos = (i - activeIdx + count) % count;

          let posClass = 'is-pos-deep';
          if (relPos === 0) posClass = 'is-pos-front';
          else if (relPos === 1) posClass = 'is-pos-second';
          else if (relPos === 2) posClass = 'is-pos-third';

          const tabColor = TAB_COLORS[i % TAB_COLORS.length];

          const dragStyle =
            relPos === 0 && dragOffset !== 0
              ? {
                  transform: `translateX(${dragOffset}px) rotate(${dragOffset * 0.08}deg)`,
                  transition: 'none',
                }
              : undefined;

          return (
            <button
              key={`${src}-${i}`}
              type="button"
              className={`card-deck-item ${posClass} ${
                relPos === 0 && isShuffling ? 'is-shuffling' : ''
              } tap`}
              style={dragStyle}
              onClick={() => handleCardTap(i)}
              aria-label={`${alt} - foto ${i + 1} de ${count}`}
            >
              <span
                className={`card-deck-tab card-deck-tab--${tabColor}`}
                aria-hidden="true"
              >
                {i + 1} / {count}
              </span>
              <img
                src={src}
                alt={`${alt} (${i + 1} de ${count})`}
                className="card-deck-img"
                loading="lazy"
                decoding="async"
              />
            </button>
          );
        })}
      </div>

      <div className="card-deck-pills" aria-hidden="true">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`chip card-deck-pill${i === activeIdx ? ' is-active' : ''}`}
            onClick={() => selectCard(i)}
          >
            {i + 1}
          </button>
        ))}
      </div>
    </div>
  );
}
